/**
 * Chart-of-accounts numbers — the TS side of the numbering model
 * (docs/CHART_OF_ACCOUNTS.md; SQL: next_account_number / coa_number_base /
 * coa_renumber_org, migration 20261006120000).
 *
 * A derived number is `<base>-<counter>[-<counter>…]`: the base is the code of
 * the account's subtype in the org's numbering scheme (PCG `613`, PCMN `61`,
 * SKR04 `6310`, UCOA `8210`, Relevé `5200`), each `-<n>` a counter. Legacy
 * charts still carry plain numbers (`4000`) or none at all.
 */
/** The numbering schemes an organization can choose (`organizations.account_numbering_scheme`). */
export const ACCOUNT_NUMBERING_SCHEMES = [
    "house",
    "pcg",
    "pcg_assoc",
    "pcmn",
    "skr03",
    "skr04",
    "ucoa",
];
export function isAccountNumberingScheme(value) {
    return typeof value === "string" && ACCOUNT_NUMBERING_SCHEMES.includes(value);
}
/**
 * The scheme recommended for an organization's country — the SAME rule the
 * Phase 3 migration used to set existing orgs (20261006120100): the two-letter
 * prefix of `COALESCE(primary_tax_jurisdiction, country)` (production stores
 * `US-ID`, never `US`). FR → PCG, BE → PCMN, DE → SKR04, US → UCOA, anything
 * else (NL, other, unset) → Relevé codes.
 */
export function recommendedNumberingScheme(primaryTaxJurisdiction, country) {
    const source = primaryTaxJurisdiction || country || "";
    switch (source.split("-")[0].toUpperCase()) {
        case "FR":
            return "pcg";
        case "BE":
            return "pcmn";
        case "DE":
            return "skr04";
        case "US":
            return "ucoa";
        default:
            return "house";
    }
}
/**
 * The derived account-number shape `<digits>(-<digits>)+` — `613-1`,
 * `5200-1-2`. TS twin of SQL `public.coa_is_derived_number`.
 */
export const DERIVED_ACCOUNT_NUMBER_PATTERN = /^[0-9]+(-[0-9]+)+$/;
export function isDerivedAccountNumber(value) {
    return typeof value === "string" && DERIVED_ACCOUNT_NUMBER_PATTERN.test(value);
}
const DIGITS = /^[0-9]+$/;
function compareParts(a, b, numeric) {
    if (numeric && DIGITS.test(a) && DIGITS.test(b)) {
        // Arbitrary length without Number precision loss: strip leading zeros,
        // then length, then text.
        const x = a.replace(/^0+(?=.)/, "");
        const y = b.replace(/^0+(?=.)/, "");
        if (x.length !== y.length)
            return x.length - y.length;
    }
    return a < b ? -1 : a > b ? 1 : 0;
}
/**
 * The one account-number order.
 *
 * - The BASE (before the first `-`) is compared character by character, the
 *   way charts read: PCG `613` sorts before `62` (61x is a sub-class of 61).
 * - Every `-` part after it is compared as a number: `613-9` < `613-10`.
 * - A number that is a prefix of another comes first, so a parent sits before
 *   its children: `613-1` < `613-1-2` < `613-2`.
 * - `null` / empty (legacy and template rows) go last unless `nulls: 'first'`.
 *
 * Returns 0 for equal numbers — callers add their own tie-break (name, id).
 */
export function compareAccountNumbers(a, b, options = {}) {
    const aMissing = a == null || a === "";
    const bMissing = b == null || b === "";
    if (aMissing || bMissing) {
        if (aMissing && bMissing)
            return 0;
        const missingFirst = options.nulls === "first";
        return aMissing === missingFirst ? -1 : 1;
    }
    const left = a.split("-");
    const right = b.split("-");
    const shared = Math.min(left.length, right.length);
    for (let i = 0; i < shared; i += 1) {
        const byPart = compareParts(left[i], right[i], i > 0);
        if (byPart !== 0)
            return byPart;
    }
    return left.length - right.length;
}
/**
 * The account label every picker, grid cell and register shows:
 * `"<number> <name>"` (`613-1 Rent`) when numbers are shown and the account
 * has one, otherwise just the name. A space, not " - ": the derived numbers
 * carry hyphens themselves. `show` is the org's `show_account_numbers`.
 */
export function formatAccountLabel(account, show) {
    return show && account.account_number
        ? `${account.account_number} ${account.account_name}`
        : account.account_name;
}
//# sourceMappingURL=accountNumbers.js.map